import { useState } from "react";
import { Smartphone, Share, PlusSquare, X, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

let deferredPrompt = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    window.dispatchEvent(new Event("pwa-installable"));
  });
}

const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

export const usePWAInstall = () => {
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") deferredPrompt = null;
      return;
    }
    setShowIOSGuide(true);
  };

  return { install, showIOSGuide, setShowIOSGuide };
};

export const IOSInstallGuide = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-sm" data-testid="ios-install-guide">
      <DialogHeader><DialogTitle className="flex items-center gap-2"><Smartphone className="h-5 w-5 text-primary" />Install Opero Smart</DialogTitle></DialogHeader>
      <div className="space-y-3 text-sm">
        <p className="text-muted-foreground">These steps use your phone's browser buttons (not this popup):</p>
        <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
          <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center shrink-0">1</span>
          <span>Look at <b>Safari's toolbar at the very bottom of your screen</b> and tap the <b>Share</b> button <Share className="h-4 w-4 inline text-primary" /> (square with an arrow pointing up)</span>
        </div>
        <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
          <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center shrink-0">2</span>
          <span>In the menu that slides up, scroll down and tap <b>"Add to Home Screen"</b> <PlusSquare className="h-4 w-4 inline text-primary" /></span>
        </div>
        <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
          <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center shrink-0">3</span>
          <span>Tap <b>Add</b> (top right) — the Opero Smart icon appears on your home screen like a real app</span>
        </div>
        <p className="text-xs text-muted-foreground">On Android? Open the browser menu (⋮ top right) and tap <b>"Install app"</b> or <b>"Add to Home screen"</b>.</p>
        <div className="flex flex-col items-center pt-1 text-primary">
          <span className="text-xs font-semibold mb-1">The Share button is down here in Safari's bar</span>
          <svg className="h-8 w-8 animate-bounce" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12l7 7 7-7"/></svg>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export const InstallAppButton = ({ className = "", variant = "outline", size = "lg" }) => {
  const { install, showIOSGuide, setShowIOSGuide } = usePWAInstall();
  return (
    <>
      <Button onClick={install} variant={variant} size={size} className={className} data-testid="install-app-btn">
        <Download className="h-5 w-5 mr-2" />Get the App
      </Button>
      <IOSInstallGuide open={showIOSGuide} onOpenChange={setShowIOSGuide} />
    </>
  );
};

export const PWAInstallBanner = () => {
  const { install, showIOSGuide, setShowIOSGuide } = usePWAInstall();
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("pwa_banner_dismissed") === "1");
  if (dismissed || isStandalone()) return null;
  return (
    <>
      <div style={{ position: "fixed", zIndex: 60 }} className="lg:hidden bottom-16 left-3 right-3 bg-card border shadow-lg rounded-xl p-3 flex items-center gap-3" data-testid="pwa-install-banner">
        <img src="/favicon-96.png" alt="Opero Smart" className="h-10 w-10 rounded-xl shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">Get the Opero Smart app</p>
          <p className="text-xs text-muted-foreground truncate">Install it on your home screen — fast & fullscreen</p>
        </div>
        <Button size="sm" onClick={install} data-testid="pwa-banner-install-btn">Install</Button>
        <button onClick={() => { localStorage.setItem("pwa_banner_dismissed", "1"); setDismissed(true); }} className="p-1 text-muted-foreground hover:text-foreground" data-testid="pwa-banner-dismiss-btn"><X className="h-4 w-4" /></button>
      </div>
      <IOSInstallGuide open={showIOSGuide} onOpenChange={setShowIOSGuide} />
    </>
  );
};
